import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import Header from '../components/Header';
import { useApp } from '../context/AppContext';

const DAY_NAMES = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];

const CalendarScreen = ({ navigate }) => {
  const { dailyLogs } = useApp();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const monthName = currentDate.toLocaleDateString('he-IL', {
    month: 'long',
    year: 'numeric',
  });

  const getDayStatus = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (dailyLogs[dateStr]) return dailyLogs[dateStr].isClean ? 'clean' : 'lapse';
    return null;
  };

  const STATUS_STYLES = {
    clean: 'bg-brand-500 text-white',
    lapse: 'bg-bloom-500 text-white',
    skip: 'bg-stone-300 text-stone-600',
  };

  const cells = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const prevMonth = () => setCurrentDate(new Date(year, month - 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1));

  const cleanDays = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(d => getDayStatus(d) === 'clean').length;
  const lapseDays = Array.from({ length: daysInMonth }, (_, i) => i + 1).filter(d => getDayStatus(d) === 'lapse').length;

  return (
    <div dir="rtl" className="min-h-screen bg-stone-50 pb-24">
      <Header
        title="לוח שנה"
        subtitle="עקוב אחרי כל יום"
        rightAction={
          navigate && (
            <button onClick={() => navigate('home')} className="p-2 hover:bg-stone-100 rounded-lg">
              <X className="w-5 h-5 text-stone-500" />
            </button>
          )
        }
      />

      <div className="px-4 py-4">
        {/* Month Navigation */}
        <div className="bg-white rounded-xl p-4 shadow mb-4">
          <div className="flex justify-between items-center mb-4">
            <button onClick={prevMonth} className="p-2 hover:bg-stone-100 rounded-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold text-stone-800 font-display">{monthName}</h2>
            <button onClick={nextMonth} className="p-2 hover:bg-stone-100 rounded-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
          </div>

          {/* Legend */}
          <div className="flex justify-around mb-4 text-xs">
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-brand-500" /><span>יום נקי</span></div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-bloom-500" /><span>נפילה</span></div>
            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-stone-300" /><span>לא סומן</span></div>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-xs font-bold text-stone-500 py-1">{d}</div>
            ))}
          </div>

          {/* Day Cells */}
          <div className="grid grid-cols-7 gap-1">
            {cells.map((day, idx) => {
              const status = day ? getDayStatus(day) : null;
              return (
                <button
                  key={idx}
                  onClick={() => day && setSelectedDay(day)}
                  className={`aspect-square rounded-lg text-sm font-semibold transition-all ${
                    !day ? 'invisible' :
                    status ? STATUS_STYLES[status] : 'bg-stone-100 text-stone-400'
                  } ${selectedDay === day ? 'ring-2 ring-brand-400 scale-110' : ''}`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Day Detail */}
        {selectedDay && (
          <div className="bg-brand-50 rounded-xl p-4 border border-brand-200 mb-4">
            <h3 className="font-bold text-stone-800 mb-2">יום {selectedDay} {monthName}</h3>
            <p className="text-sm text-stone-700">
              <span className="font-semibold">סטטוס: </span>
              {getDayStatus(selectedDay) === 'clean' ? '✅ יום נקי' :
               getDayStatus(selectedDay) === 'lapse' ? '💜 נפילה' : '⬜ לא סומן'}
            </p>
          </div>
        )}

        {/* Monthly Summary */}
        <div className="bg-white rounded-xl p-4 shadow">
          <h3 className="font-bold text-stone-800 mb-3">📊 סיכום החודש</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-brand-50 rounded-lg p-3 border border-brand-200">
              <p className="text-2xl font-bold text-brand-600">{cleanDays}</p>
              <p className="text-xs text-brand-700">ימים נקיים</p>
            </div>
            <div className="bg-bloom-50 rounded-lg p-3 border border-bloom-200">
              <p className="text-2xl font-bold text-bloom-500">{lapseDays}</p>
              <p className="text-xs text-bloom-700">נפילות</p>
            </div>
            <div className="bg-brand-50 rounded-lg p-3 border border-brand-200 col-span-2">
              <p className="text-2xl font-bold text-brand-600">
                {cleanDays + lapseDays > 0 ? Math.round((cleanDays / (cleanDays + lapseDays)) * 100) : 0}%
              </p>
              <p className="text-xs text-brand-700">שיעור הצלחה</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarScreen;
